/**
 * SectionForm — rend une section du rapport à partir de sa configuration (_report.js) : groupes de champs, listes de texte, tableaux, zones de texte.
 */
import { Calculator } from "lucide-react";
import { Btn, inputCls } from "../../comptabilite/_shared";
import ListEditor from "./ListEditor";
import { getPath, setPath } from "./_report";

function Field({ field, report, onChange }) {
  const v = getPath(report, field.path) ?? "";
  const id = `tr-${field.path.replace(/\./g, "-")}`;
  return (
    <label className={`block ${field.span === 2 ? "sm:col-span-2" : ""}`}>
      <span className="block text-[11px] font-semibold text-slate-600 mb-1">{field.label}</span>
      {field.type === "select" ? (
        <select value={v} onChange={(e) => onChange(field.path, e.target.value)} className={inputCls} data-testid={id}>
          <option value="">—</option>
          {v && !field.options.includes(v) && <option value={v}>{v}</option>}
          {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input type={field.type === "date" ? "date" : field.type === "number" ? "text" : "text"} inputMode={field.type === "number" ? "decimal" : undefined} value={v}
          onChange={(e) => onChange(field.path, e.target.value)} className={inputCls} data-testid={id} />
      )}
    </label>
  );
}

function StrList({ path, placeholder, report, onChange }) {
  const lines = (getPath(report, path) || []).join("\n");
  return (
    <textarea rows={4} value={lines} placeholder={placeholder} className={inputCls} data-testid={`tr-${path.replace(/\./g, "-")}`}
      onChange={(e) => onChange(path, e.target.value.split("\n"))} onBlur={(e) => onChange(path, e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))} />
  );
}

export default function SectionForm({ section, report, setReport, onComputeRatios }) {
  const onChange = (path, value) => setReport((r) => setPath(r, path, value));
  return (
    <div className="space-y-5" data-testid={`tr-section-${section.id}`}>
      {section.hint && <p className="text-xs text-slate-500">{section.hint}</p>}
      {section.groups.map((g) => (
        <section key={g.title} className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3" data-testid={`tr-group-${g.title.slice(0, 20).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h3 className="text-sm font-bold text-slate-800">{g.title}</h3>
            {g.ratios && <Btn tone="slate" icon={Calculator} onClick={onComputeRatios} testId="tr-compute-ratios" title="Calcule les % à partir du loyer, des paiements de dette et du revenu du ménage">Calculer les ratios</Btn>}
          </div>
          {g.fields && <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">{g.fields.map((fl) => <Field key={fl.path} field={fl} report={report} onChange={onChange} />)}</div>}
          {g.strlist && <StrList path={g.strlist} placeholder={g.placeholder} report={report} onChange={onChange} />}
          {g.textarea && (
            <label className="block">
              <span className="block text-[11px] font-semibold text-slate-600 mb-1">{g.textarea.label}</span>
              <textarea rows={g.textarea.rows || 3} value={getPath(report, g.textarea.path) ?? ""} onChange={(e) => onChange(g.textarea.path, e.target.value)} className={inputCls} data-testid={`tr-${g.textarea.path.replace(/\./g, "-")}`} />
            </label>
          )}
          {g.table && <ListEditor table={g.table} rows={getPath(report, g.table.path)} onChange={(rows) => onChange(g.table.path, rows)} testId={`tr-list-${g.table.path.replace(/\./g, "-")}`} />}
        </section>
      ))}
    </div>
  );
}
